import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomers, addCustomer, updateCustomer, removeCustomer, blankCustomer, PAYMENT_METHODS, PLANS } from '../customerStore';
import { getDevices, updateDevice, getUsers, adminCreateUser, requestPasswordReset, deviceOwners, setDeviceOwners, appConfigFor, adminSaveUserConfig, updateUser } from '../traccar';
import { TAGALONG_ACCESS } from '../product';
import { getVehiclePhotos, savePhoto, removePhoto, ALL_SLOTS } from '../photoStore';
import { VEHICLE_YEARS, VEHICLE_MAKES, VEHICLE_COLORS, modelsForMake } from '../vehicleOptions';
import AddressAutocomplete from '../AddressAutocomplete';
import { playOpen } from '../clickSound';
import PhotoPicker from '../PhotoPicker';

const lbl = { fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '4px' };
const inp = { padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', width: '100%', boxSizing: 'border-box', background: '#fff' };
const card = { background: 'white', borderRadius: '12px', padding: '18px', boxShadow: '0 1px 3px rgba(15,23,42,0.06)', border: '1px solid #e5e7eb' };
const section = { background: '#f6f9fc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '12px' };

export default function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState(getCustomers());
  const [devices, setDevices] = useState([]);
  const [users, setUsers] = useState([]); // Traccar logins, to save per-profile app config
  const [q, setQ] = useState('');
  const [selId, setSelId] = useState(null);
  const [form, setForm] = useState(null);
  const refresh = () => setCustomers(getCustomers());

  useEffect(() => {
    const pull = async () => {
      getDevices().then(setDevices).catch(() => {});
      // auto-import self-registered Traccar users into the CRM (once, by email)
      try {
        const users = await getUsers();
        setUsers(users);
        // Match against BOTH the email and the Traccar user id already in the CRM,
        // so we don't miss/duplicate an account whose CRM record has no email.
        const existing = new Set(getCustomers().map((c) => (c.email || '').toLowerCase()).filter(Boolean));
        const haveUid = new Set(getCustomers().map((c) => (c.traccarUserId != null ? String(c.traccarUserId) : '')).filter(Boolean));
        let added = 0;
        users.forEach((u) => {
          // Show EVERY login here (including admin/operator accounts) so the
          // operator can manage any account's devices. Was skipping admins,
          // which hid the operator's own account.
          const em = (u.email || '').toLowerCase();
          if (haveUid.has(String(u.id)) || (em && existing.has(em))) return;
          addCustomer({ name: u.name || u.email || `User ${u.id}`, email: u.email || '', phone: (u.attributes && u.attributes.phone) || u.phone || '', status: 'Customer', services: { tagalong: true, dispatch: false }, plan: (u.attributes && u.attributes.plan) || 'none', traccarUserId: u.id });
          if (em) existing.add(em); haveUid.add(String(u.id)); added += 1;
        });
        if (added) refresh();
      } catch { /* not admin / offline — skip import */ }
      setCustomers(getCustomers());
    };
    pull();
    // re-pull whenever the window/tab regains focus so edits made elsewhere show up
    const onFocus = () => { if (!document.hidden) pull(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => { window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, []);

  const shown = (() => {
    const s = q.trim().toLowerCase();
    const list = customers;
    return s ? list.filter((c) => `${c.name} ${c.account} ${c.phone} ${c.email} ${c.company}`.toLowerCase().includes(s)) : list;
  })();

  const open = (c) => { playOpen(); setSelId(c.id); setForm({ ...blankCustomer(), ...c }); };
  const startNew = () => { setSelId('new'); setForm(blankCustomer()); };
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setSvc = (k, v) => setForm((f) => ({ ...f, services: { ...(f.services || {}), [k]: v } }));

  const openInIntake = (d) => navigate(`/intake?device=${encodeURIComponent(d.uniqueId)}`);
  // Vehicle profile is shared with the Vehicles tab: descriptive fields live on
  // the device attributes, photos live in the shared photoStore (same slots).
  const [expandDev, setExpandDev] = useState(null); // device id whose profile is open
  const [vehInfo, setVehInfo] = useState({}); // editable info buffer for the open device
  const [vehPhotos, setVehPhotos] = useState({}); // slot -> dataUrl for the open device
  const readInfo = (d) => { const a = (d && d.attributes) || {}; return { year: a.vehYear || '', make: a.vehMake || '', model: a.vehModel || '', color: a.vehColor || '', plate: a.vehPlate || '', plateExp: a.vehPlateExp || '' }; };
  const toggleDev = async (d) => {
    if (expandDev === d.id) { setExpandDev(null); return; }
    setExpandDev(d.id); setVehInfo(readInfo(d));
    setVehPhotos(await getVehiclePhotos(d.id).catch(() => ({})));
  };
  const saveInfo = async (d, info) => {
    const updated = { ...d, attributes: { ...(d.attributes || {}), vehYear: info.year, vehMake: info.make, vehModel: info.model, vehColor: info.color, vehPlate: info.plate, vehPlateExp: info.plateExp } };
    setDevices((list) => list.map((x) => (x.id === d.id ? updated : x)));
    try { await updateDevice(updated); } catch { alert('Could not save the vehicle profile.'); }
  };
  const addPhoto = async (d, dataUrl) => {
    const slot = ALL_SLOTS.find((s) => !vehPhotos[s]);
    if (!slot) { alert('Photo limit reached — remove one first.'); return; }
    await savePhoto(d.id, slot, dataUrl); setVehPhotos(await getVehiclePhotos(d.id));
  };
  const rmPhoto = async (d, slot) => { await removePhoto(d.id, slot); setVehPhotos(await getVehiclePhotos(d.id)); };

  const save = () => {
    if (!form.name.trim()) { alert('Enter the customer name.'); return; }
    if (selId === 'new') { const rec = addCustomer(form); refresh(); setSelId(rec.id); setForm({ ...blankCustomer(), ...rec }); alert(`Customer created — account #${rec.account}.`); }
    else {
      updateCustomer(selId, form); refresh();
      // keep the account number stamped on their login so it shows in-app
      if (form.traccarUserId) {
        const u = users.find((x) => String(x.id) === String(form.traccarUserId));
        if (u && (!u.attributes || u.attributes.account !== form.account)) {
          const stamped = { ...u, attributes: { ...(u.attributes || {}), account: form.account } };
          updateUser(stamped).then(() => setUsers((list) => list.map((x) => (String(x.id) === String(u.id) ? stamped : x)))).catch(() => {});
        }
      }
    }
  };
  const del = () => { if (window.confirm(`Delete ${form.name}? This cannot be undone.`)) { removeCustomer(selId); refresh(); setSelId(null); setForm(null); } };

  // Track 1 (phone-initiated): create the customer's Traccar login with a temp
  // password. They're forced to set their own password on first sign-in.
  const [creds, setCreds] = useState(null); // { email, tempPassword } to relay
  const createLogin = async () => {
    if (selId === 'new') { alert('Save the customer first, then create their login.'); return; }
    if (!form.email.trim()) { alert('Add an email first — it becomes their username.'); return; }
    try {
      const { user, tempPassword } = await adminCreateUser({ name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() });
      // Stamp the account number onto their login so they can see it in-app.
      try {
        const stamped = { ...user, attributes: { ...(user.attributes || {}), account: form.account } };
        await updateUser(stamped);
        setUsers((list) => [...list.filter((u) => String(u.id) !== String(user.id)), stamped]);
      } catch { /* non-fatal */ }
      updateCustomer(selId, { traccarUserId: user.id, status: 'Customer' });
      setForm((f) => ({ ...f, traccarUserId: user.id, status: 'Customer' })); refresh();
      setCreds({ email: form.email.trim(), tempPassword });
    } catch (e) { alert(e.message); }
  };

  // ---- device assignment (which cars this customer's account can see) ----
  const [assignBusy, setAssignBusy] = useState(null); // device id mid-toggle
  // Assignment uses the SAME multi-owner model as the Devices tab
  // (attributes.owners + memberLinks). This is what makes the toggle persist and
  // keeps the two pages in sync — previously Customers wrote a single ownerUserId
  // that the owners[]-based scoping ignored/overwrote, so it looked "unsaved".
  const matchesThis = (o) => {
    const uid = form && form.traccarUserId ? String(form.traccarUserId) : '';
    return (uid && String(o.userId) === uid) || (selId && String(o.customerId) === String(selId));
  };
  const isAssigned = (d) => deviceOwners(d).some(matchesThis);
  const toggleAssign = async (d) => {
    if (selId === 'new') { alert('Save the customer first, then assign devices.'); return; }
    setAssignBusy(d.id);
    try {
      const owners = deviceOwners(d);
      // Always record the STABLE Traccar user id (the key the alert server matches
      // on). If the CRM record isn't linked yet, resolve it from the matching
      // login by email — so a device assigned before the login was linked still
      // routes alerts correctly. This is what keeps it working for every new
      // customer regardless of the order things were set up.
      const resolvedUid = form.traccarUserId
        || (users.find((u) => u.email && form.email && u.email.toLowerCase() === form.email.toLowerCase()) || {}).id
        || '';
      const next = isAssigned(d)
        ? owners.filter((o) => !matchesThis(o)) // remove this account, keep any others
        : [...owners, { userId: resolvedUid || '', customerId: selId, account: form.account || '', name: form.name || '' }];
      const updated = await setDeviceOwners(d, next);
      setDevices((list) => list.map((x) => (x.id === d.id ? updated : x)));
    } catch { alert('Could not update that device assignment.'); }
    finally { setAssignBusy(null); }
  };

  // ---- send a password-reset link to the customer ----
  const sendReset = async () => {
    if (!form.email.trim()) { alert('Add an email first.'); return; }
    try { await requestPasswordReset(form.email.trim()); alert(`Password-reset link sent to ${form.email.trim()} (if email is configured on the server).`); }
    catch (e) { alert(e.message); }
  };

  // ---- per-profile app access (what this account sees / can open) ----
  const userFor = (c) => users.find((u) => c && c.traccarUserId && String(u.id) === String(c.traccarUserId));
  const currentUser = form ? userFor(form) : null;
  const currentCfg = currentUser ? appConfigFor(currentUser) : { hidden: [] };
  const [cfgBusy, setCfgBusy] = useState(false);
  const toggleAccess = async (path) => {
    if (!currentUser) { alert('Create the login first, then set what they can see.'); return; }
    const hidden = new Set(currentCfg.hidden || []);
    if (hidden.has(path)) hidden.delete(path); else hidden.add(path);
    const next = { ...currentCfg, hidden: Array.from(hidden) };
    setCfgBusy(true);
    try {
      const saved = await adminSaveUserConfig(currentUser, next);
      setUsers((list) => list.map((u) => (String(u.id) === String(saved.id) ? saved : u)));
    } catch { alert('Could not save profile access.'); }
    finally { setCfgBusy(false); }
  };

  // Count/list a customer's devices by the SAME ownership signals used for
  // scoping and alerts — user id, member link, customer id, or account number —
  // not just attributes.customerId. Otherwise cars assigned by user id (most of
  // them) don't count and the totals read low.
  const custDevices = (id) => {
    const c = customers.find((x) => x.id === id) || (form && form.id === id ? form : null);
    const uid = c && c.traccarUserId != null ? String(c.traccarUserId) : '';
    const acct = c && c.account ? String(c.account) : '';
    // SINGLE SOURCE OF TRUTH — a car counts for a customer ONLY if that customer
    // is an OWNER (in the car's owners[]; deviceOwners also synthesizes it for
    // legacy single-owner cars). This is the exact rule the Devices-tab owner
    // chips use, so the count and the chips ALWAYS match. We deliberately do NOT
    // count family/broker share links here — a shared car is not "assigned to"
    // that customer, and counting it made a customer show a device that wasn't
    // on any of their owner chips (e.g. an old owner left with a lingering link).
    return devices.filter((d) => deviceOwners(d).some((o) => o && (
      (uid && String(o.userId) === uid)
      || (String(o.customerId) === String(id))
      || (acct && o.account && String(o.account) === acct)
    )));
  };
  // Cars SHARED with this account (added via a share code) — an approved,
  // non-owner memberLink. Not owned, but they can view it, so we surface it so
  // the Customers tab agrees with the "Added users" row on the Devices tab.
  const custShared = (id) => {
    const c = customers.find((x) => x.id === id) || (form && form.id === id ? form : null);
    const uid = c && c.traccarUserId != null ? String(c.traccarUserId) : '';
    if (!uid) return [];
    const memberId = `u${uid}`;
    return devices.filter((d) => {
      const a = d.attributes || {};
      const isOwner = deviceOwners(d).some((o) => o && String(o.userId) === uid);
      if (isOwner) return false;
      return Array.isArray(a.memberLinks) && a.memberLinks.some((m) => m && m.memberId === memberId && m.status === 'approved' && m.by !== 'admin');
    });
  };
  const initials = (n) => (n || '?').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}><h1>👥 Customers</h1><p>Every customer's full profile — contact, billing, plan, services, and devices. Synced with Device Intake.</p></div>
        <button onClick={startNew} style={{ background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', fontWeight: 700, fontSize: '13px', border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}>➕ New customer</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 320px) 1fr', gap: '16px', alignItems: 'start' }}>
        {/* list */}
        <div style={card}>
          <input style={{ ...inp, marginBottom: '10px' }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Search name, account #, phone, email" />
          <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>{shown.length} of {customers.length}</div>
          {shown.length === 0 && <div style={{ fontSize: '12px', color: '#94a3b8' }}>No customers{q ? ' match' : ' yet'}.</div>}
          {shown.map((c) => (
            <div key={c.id} onClick={() => open(c)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 8px', borderRadius: '9px', cursor: 'pointer', marginBottom: '4px', background: selId === c.id ? '#eff6ff' : 'transparent', border: `1px solid ${selId === c.id ? '#bfdbfe' : 'transparent'}` }}>
              {c.photo ? <img src={c.photo} alt="" style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover' }} />
                : <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '13px' }}>{initials(c.name)}</div>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name || '(no name)'}</div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>#{c.account} · {custDevices(c.id).length} device(s){custShared(c.id).length ? ` · ${custShared(c.id).length} shared` : ''}</div>
              </div>
              {c.plan && c.plan !== 'none' && <span style={{ fontSize: '9px', fontWeight: 700, color: c.plan === 'talk' ? '#6d28d9' : '#0369a1', background: c.plan === 'talk' ? '#f5f3ff' : '#f0f9ff', borderRadius: '999px', padding: '2px 7px' }}>{c.plan === 'talk' ? 'Talk' : 'Track'}</span>}
            </div>
          ))}
        </div>

        {/* editor */}
        {!form ? (
          <div style={{ ...card, color: '#94a3b8', fontSize: '14px', textAlign: 'center', padding: '60px 20px' }}>Select a customer on the left, or create a new one.</div>
        ) : (
          <div style={card}>
            {/* identity */}
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '14px' }}>
              <PhotoPicker facing="user" onPhoto={(url) => set({ photo: url })}>
                <span style={{ position: 'relative', display: 'inline-block' }} title="Take or choose a profile picture">
                  {form.photo ? <img src={form.photo} alt="" style={{ width: '72px', height: '72px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #f8703c' }} />
                    : <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '24px' }}>{initials(form.name)}</div>}
                  <span style={{ position: 'absolute', bottom: 0, right: 0, background: '#0f172a', color: 'white', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px' }}>✎</span>
                </span>
              </PhotoPicker>
              <div style={{ flex: 1 }}>
                <input style={{ ...inp, fontSize: '18px', fontWeight: 800, fontFamily: 'Poppins, sans-serif', marginBottom: '6px' }} value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Full name" />
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12px', color: '#64748b' }}>
                  {selId !== 'new' && <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#0066ff' }}>#{form.account}</span>}
                  <select style={{ ...inp, width: 'auto', padding: '4px 8px' }} value={form.status} onChange={(e) => set({ status: e.target.value })}><option>Interested</option><option>Customer</option></select>
                </div>
              </div>
            </div>

            {/* services toggle */}
            <div style={section}>
              <span style={lbl}>Services</span>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {[['tagalong', '🚗 TagAlong'], ['dispatch', '🚦 Dynamic Dispatch']].map(([k, label]) => {
                  const on = !!(form.services || {})[k];
                  return <button key={k} onClick={() => setSvc(k, !on)} style={{ background: on ? (k === 'tagalong' ? '#fff7ed' : '#eff6ff') : '#fff', color: on ? (k === 'tagalong' ? '#c2410c' : '#1d4ed8') : '#94a3b8', border: `1px solid ${on ? (k === 'tagalong' ? '#fed7aa' : '#bfdbfe') : '#e2e8f0'}`, borderRadius: '999px', padding: '8px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>{on ? '✓ ' : ''}{label}</button>;
                })}
              </div>
            </div>

            {/* contact */}
            <div style={section}>
              <span style={lbl}>Contact</span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div><span style={lbl}>Company (optional)</span><input style={inp} value={form.company} onChange={(e) => set({ company: e.target.value })} /></div>
                <div><span style={lbl}>Preferred language</span><select style={inp} value={form.lang} onChange={(e) => set({ lang: e.target.value })}><option value="en">English</option><option value="es">Español</option></select></div>
                <div><span style={lbl}>Mobile phone</span><input style={inp} value={form.phone} onChange={(e) => set({ phone: e.target.value })} /></div>
                <div><span style={lbl}>Alt phone</span><input style={inp} value={form.altPhone} onChange={(e) => set({ altPhone: e.target.value })} /></div>
                <div><span style={lbl}>Email</span><input style={inp} value={form.email} onChange={(e) => set({ email: e.target.value })} /></div>
                <div><span style={lbl}>Alt email</span><input style={inp} value={form.altEmail} onChange={(e) => set({ altEmail: e.target.value })} /></div>
              </div>
            </div>

            {/* customer login (Track 1: phone-initiated) */}
            <div style={section}>
              <span style={lbl}>Customer login</span>
              {form.traccarUserId ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: '#16a34a', fontWeight: 700 }}>✓ Login active</span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>{form.email}</span>
                  <button onClick={createLogin} style={{ background: 'white', color: '#0066ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>Re-issue temp password</button>
                  <button onClick={sendReset} style={{ background: 'white', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>✉️ Send reset link</button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <button onClick={createLogin} style={{ background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>🔑 Create login & temp password</button>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>They'll be forced to set their own password on first sign-in.</span>
                </div>
              )}
            </div>

            {/* assign devices — which cars this account sees when they log in */}
            {selId !== 'new' && (
              <div style={section}>
                <span style={lbl}>Assign devices to this account ({devices.filter(isAssigned).length} of {devices.length})</span>
                <div style={{ fontSize: '11px', color: '#64748b', margin: '0 0 8px' }}>Toggle a vehicle on to make it appear in this customer's app the next time they open it. Toggle off to remove their access.</div>
                {devices.length === 0 && <div style={{ fontSize: '12px', color: '#94a3b8' }}>No devices on the server yet.</div>}
                <div style={{ maxHeight: '260px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  {devices.map((d) => {
                    const on = isAssigned(d);
                    const busy = assignBusy === d.id;
                    return (
                      <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '9px', border: `1px solid ${on ? '#bbf7d0' : '#e5e7eb'}`, background: on ? '#f0fdf4' : 'white' }}>
                        <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: d.status === 'online' ? '#22c55e' : '#cbd5e1' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}{(d.attributes || {}).displayName ? ` · “${d.attributes.displayName}”` : ''}</div>
                          <div style={{ fontSize: '10px', color: '#94a3b8', fontFamily: 'monospace' }}>IMEI {d.uniqueId}</div>
                        </div>
                        <button onClick={() => toggleAssign(d)} disabled={busy} title={on ? 'Remove from this account' : 'Add to this account'}
                          style={{ position: 'relative', width: '44px', height: '24px', borderRadius: '999px', border: 'none', cursor: busy ? 'default' : 'pointer', background: on ? '#16a34a' : '#cbd5e1', transition: 'background .15s', opacity: busy ? 0.6 : 1 }}>
                          <span style={{ position: 'absolute', top: '2px', left: on ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', background: 'white', transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }} />
                        </button>
                      </div>
                    );
                  })}
                </div>
                {!form.traccarUserId && <div style={{ fontSize: '11px', color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '7px 9px', marginTop: '8px' }}>⚠️ This customer has no login yet — assignments are saved, but create the login above so they can actually sign in and see them.</div>}
              </div>
            )}

            {/* app access — what this profile can see / open */}
            {selId !== 'new' && (
              <div style={section}>
                <span style={lbl}>App access — what this profile sees</span>
                <div style={{ fontSize: '11px', color: '#64748b', margin: '0 0 10px' }}>{currentUser ? 'Turn a tab off to hide it from this customer when they sign in.' : 'Create the login first — then you can choose which tabs this account sees.'}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', opacity: currentUser ? 1 : 0.5, pointerEvents: currentUser && !cfgBusy ? 'auto' : 'none' }}>
                  {TAGALONG_ACCESS.map((f) => {
                    const hidden = (currentCfg.hidden || []).includes(f.path);
                    return (
                      <button key={f.path} onClick={() => toggleAccess(f.path)}
                        style={{ background: hidden ? '#fff' : '#eff6ff', color: hidden ? '#94a3b8' : '#1d4ed8', border: `1px solid ${hidden ? '#e2e8f0' : '#bfdbfe'}`, borderRadius: '999px', padding: '7px 13px', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', textDecoration: hidden ? 'line-through' : 'none' }}>
                        {hidden ? '' : '✓ '}{f.name}
                      </button>
                    );
                  })}
                </div>
                {cfgBusy && <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>Saving…</div>}
              </div>
            )}

            {/* address */}
            <div style={section}>
              <span style={lbl}>Address</span>
              <div style={{ marginBottom: '8px' }}>
                <AddressAutocomplete
                  style={inp}
                  value={form.address}
                  onChange={(v) => set({ address: v })}
                  onPickParts={(p) => set({ address: p.street || p.description, city: p.city || form.city, state: p.state || form.state, zip: p.zip || form.zip })}
                  placeholder="Street address" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px' }}>
                <input style={inp} value={form.city} onChange={(e) => set({ city: e.target.value })} placeholder="City" />
                <input style={inp} value={form.state} onChange={(e) => set({ state: e.target.value })} placeholder="State" />
                <input style={inp} value={form.zip} onChange={(e) => set({ zip: e.target.value })} placeholder="ZIP" />
              </div>
            </div>

            {/* emergency contact */}
            <div style={section}>
              <span style={lbl}>Emergency contact</span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <input style={inp} value={form.emergencyName} onChange={(e) => set({ emergencyName: e.target.value })} placeholder="Name" />
                <input style={inp} value={form.emergencyPhone} onChange={(e) => set({ emergencyPhone: e.target.value })} placeholder="Phone" />
              </div>
            </div>

            {/* plan + billing */}
            <div style={section}>
              <span style={lbl}>Membership & billing</span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div><span style={lbl}>Plan</span><select style={inp} value={form.plan} onChange={(e) => set({ plan: e.target.value })}>{PLANS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}</select></div>
                <div><span style={lbl}>Payment method</span><select style={inp} value={form.payment} onChange={(e) => set({ payment: e.target.value })}>{PAYMENT_METHODS.map((m) => <option key={m}>{m}</option>)}</select></div>
                <div><span style={lbl}>Card last 4</span><input style={inp} value={form.cardLast4} onChange={(e) => set({ cardLast4: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="4242" /></div>
                <div><span style={lbl}>Next due</span><input type="date" style={inp} value={form.nextDue} onChange={(e) => set({ nextDue: e.target.value })} /></div>
              </div>
              <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px', marginTop: '8px', cursor: 'pointer' }}><input type="checkbox" checked={!!form.autopay} onChange={(e) => set({ autopay: e.target.checked })} /> Autopay enabled</label>
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>Membership started: {form.membershipStart ? new Date(form.membershipStart).toLocaleDateString() : 'not activated yet'}</div>
            </div>

            {/* devices (read-only) */}
            {selId !== 'new' && (
              <div style={section}>
                <span style={lbl}>Devices on this account ({custDevices(selId).length})</span>
                {custDevices(selId).length === 0 && <div style={{ fontSize: '12px', color: '#94a3b8' }}>None — assign one in Device Intake.</div>}
                {custDevices(selId).map((d) => {
                  const open = expandDev === d.id;
                  const info = open ? vehInfo : readInfo(d);
                  const days = info.plateExp ? Math.ceil((new Date(info.plateExp) - new Date()) / 86400000) : null;
                  const shownPhotos = ALL_SLOTS.map((s) => [s, vehPhotos[s]]).filter(([, p]) => p);
                  return (
                    <div key={d.id} style={{ border: '1px solid #eef2f7', borderRadius: '10px', marginBottom: '6px', background: 'white', overflow: 'hidden' }}>
                      <div onClick={() => toggleDev(d)} title="Show this vehicle's profile" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px', cursor: 'pointer' }}>
                        <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: d.status === 'online' ? '#22c55e' : '#cbd5e1' }} />
                        <span style={{ fontSize: '13px', fontWeight: 700 }}>{d.name}</span>
                        {(d.attributes || {}).displayName && <span style={{ fontSize: '11px', color: '#6d28d9' }}>“{d.attributes.displayName}”</span>}
                        {days != null && <span style={{ fontSize: '9px', fontWeight: 700, borderRadius: '999px', padding: '2px 7px', color: days <= 30 ? '#b91c1c' : '#15803d', background: days <= 30 ? '#fef2f2' : '#f0fdf4' }}>🪪 {days < 0 ? 'Expired' : `${days}d`}</span>}
                        {(d.attributes || {}).simIccid && <span title={`SIM ICCID ${d.attributes.simIccid}`} style={{ fontSize: '10px', fontWeight: 700, color: '#15803d', background: '#f0fdf4', borderRadius: '999px', padding: '2px 7px', marginLeft: 'auto' }}>📶 SIM ···{String(d.attributes.simIccid).slice(-6)}</span>}
                        <span style={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'monospace', marginLeft: (d.attributes || {}).simIccid ? '0' : 'auto' }}>IMEI {d.uniqueId}</span>
                        <span style={{ color: '#7c3aed', fontWeight: 700, fontSize: '13px' }}>{open ? '▾' : '▸'}</span>
                      </div>
                      {open && (
                        <div style={{ padding: '4px 12px 14px', background: '#faf7ff', borderTop: '1px solid #f1eafe' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.4px', margin: '10px 0 8px' }}>🚘 Vehicle profile <span style={{ color: '#94a3b8', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>· synced with the Vehicles tab</span></div>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                            {shownPhotos.map(([s, p]) => (
                              <div key={s} style={{ position: 'relative' }}>
                                <img src={p} alt="" style={{ width: '92px', height: '68px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd6fe' }} />
                                <button onClick={() => rmPhoto(d, s)} style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', fontSize: '12px', cursor: 'pointer' }}>×</button>
                              </div>
                            ))}
                            {shownPhotos.length < ALL_SLOTS.length && (
                              <PhotoPicker onPhoto={(url) => addPhoto(d, url)}>
                                <div style={{ width: '92px', height: '68px', borderRadius: '8px', border: '2px dashed #c4b5fd', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#7c3aed', cursor: 'pointer', fontSize: '11px', fontWeight: 700 }}>📷<span>Add</span></div>
                              </PhotoPicker>
                            )}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            {[
                              ['year', 'Year', VEHICLE_YEARS],
                              ['make', 'Make', VEHICLE_MAKES],
                              ['model', 'Model', modelsForMake(vehInfo.make)],
                              ['color', 'Color', VEHICLE_COLORS],
                            ].map(([k, label, options]) => (
                              <div key={k}><span style={lbl}>{label}</span>
                                <input list={`cust-opts-${k}`} style={inp} value={vehInfo[k] || ''} onChange={(e) => setVehInfo({ ...vehInfo, [k]: e.target.value })} onBlur={() => saveInfo(d, vehInfo)} />
                                <datalist id={`cust-opts-${k}`}>{options.map((o) => <option key={o} value={o} />)}</datalist>
                              </div>
                            ))}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' }}>
                            <div><span style={lbl}>License plate</span><input style={{ ...inp, textTransform: 'uppercase', fontWeight: 700 }} value={vehInfo.plate || ''} onChange={(e) => setVehInfo({ ...vehInfo, plate: e.target.value.toUpperCase() })} onBlur={() => saveInfo(d, vehInfo)} placeholder="ABC1234" /></div>
                            <div><span style={lbl}>Registration expires</span><input type="date" style={inp} value={vehInfo.plateExp || ''} onChange={(e) => { const next = { ...vehInfo, plateExp: e.target.value }; setVehInfo(next); saveInfo(d, next); }} /></div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                            <span style={{ fontSize: '11px', color: '#7c3aed', flex: 1 }}>🔔 An alert fires a month before the registration expires.</span>
                            <button onClick={() => openInIntake(d)} style={{ background: '#eef2ff', color: '#1d4ed8', border: 'none', borderRadius: '6px', padding: '5px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>Device settings ›</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '6px' }}>Click a device to open its vehicle profile — photos and details sync with the Vehicles tab.</div>
              </div>
            )}

            {/* cars SHARED with this account via a code — view access, not owned */}
            {selId !== 'new' && custShared(selId).length > 0 && (
              <div style={section}>
                <span style={lbl}>👁 Shared with this account ({custShared(selId).length})</span>
                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px' }}>Added with a share code by the owner — they can view these cars but don't own them.</div>
                {custShared(selId).map((d) => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 9px', border: '1px solid #bfdbfe', background: '#eff6ff', borderRadius: '10px', marginBottom: '6px' }}>
                    <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: d.status === 'online' ? '#22c55e' : '#cbd5e1' }} />
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#1d4ed8' }}>{d.name}</span>
                    {(d.attributes || {}).displayName && <span style={{ fontSize: '11px', color: '#1d4ed8' }}>“{d.attributes.displayName}”</span>}
                    <span style={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'monospace', marginLeft: 'auto' }}>IMEI {d.uniqueId}</span>
                  </div>
                ))}
              </div>
            )}

            <div><span style={lbl}>Notes</span><textarea style={{ ...inp, minHeight: '54px' }} value={form.notes} onChange={(e) => set({ notes: e.target.value })} /></div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              {selId !== 'new' && <button onClick={del} style={{ background: '#fef2f2', color: '#b91c1c', fontWeight: 700, border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}>Delete</button>}
              <button onClick={save} style={{ flex: 1, background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', fontWeight: 700, border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}>{selId === 'new' ? '➕ Create customer' : '💾 Save changes'}</button>
            </div>
          </div>
        )}
      </div>

      {/* temp-password modal — shown right after creating a customer login */}
      {creds && (
        <div onClick={() => setCreds(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5000, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', padding: '24px', width: 'min(420px, 96vw)', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: '0 0 6px' }}>🔑 Login created</h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginTop: 0 }}>Send these to the customer. They'll set their own password on first sign-in.</p>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', fontFamily: 'monospace', fontSize: '14px' }}>
              <div><b>Site:</b> mytagalong.app</div>
              <div><b>Email:</b> {creds.email}</div>
              <div><b>Temp password:</b> {creds.tempPassword}</div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <button onClick={() => { navigator.clipboard && navigator.clipboard.writeText(`Site: mytagalong.app\nEmail: ${creds.email}\nTemporary password: ${creds.tempPassword}`); }} style={{ flex: 1, background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px', cursor: 'pointer' }}>📋 Copy</button>
              <button onClick={() => setCreds(null)} style={{ flex: 1, background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', fontWeight: 700, border: 'none', borderRadius: '8px', padding: '10px', cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
